import { Grid } from '@design-system';
import { isUpcoming } from '@utils/product';
import SbEditable from 'storyblok-react';

import { useIsSubscribed } from '../hooks/subscription/useIsSubscribed';
import { useProducts } from '../hooks/useProducts';
import {
  GeneratedSubscriptionType,
  useGetDrawByProductIDsQuery,
} from '../types/generated';
import { ProductListStoryblok } from '../types/generated-storyblok';
import {
  shopifyAdminIdToGraphqlId,
  shopifyGraphqlIdToAdminId,
} from '../utils/shopifyIds';
import ProductTile from './ProductTile';

interface Blok extends ProductListStoryblok {
  analytics?: Analytics;
}

export const ContentBlockProducts = ({ blok }: { blok: Blok }) => {
  const productIds = (blok as ProductIds).products.map(
    (productId: ShopifyProductIdentifier) =>
      shopifyAdminIdToGraphqlId(productId.id),
  );
  const { products } = useProducts(productIds);
  const productIDs = products.map((p) => shopifyGraphqlIdToAdminId(p.id));
  const { data: draws } = useGetDrawByProductIDsQuery({
    variables: { productIDs },
    skip: productIDs.length === 0,
  });

  const upcomingProducts = products.filter((product) => isUpcoming(product));

  const { checkIsSubscribed, refetch } = useIsSubscribed(
    upcomingProducts.map((product) => ({
      type: GeneratedSubscriptionType.Product,
      objectId: shopifyGraphqlIdToAdminId(product.id) || '',
    })),
  );

  return (
    <Grid
      id={blok._uid}
      className="scroll-m-20 gap-y-6 md:scroll-m-24"
      columns={4}
    >
      {/* @ts-expect-error require storyblok-react upgrade */}
      <SbEditable content={blok}>
        {products.map((product: Product, i: number) => {
          const draw = draws?.getDrawByProductIDs?.find(
            (d) => d.productId === shopifyGraphqlIdToAdminId(product.id),
          );

          return (
            <ProductTile
              key={product.id}
              analytics={
                blok.analytics && {
                  page: blok.analytics.page,
                  context: blok.analytics.context,
                  position: i,
                }
              }
              draw={draw}
              isSubscribed={checkIsSubscribed(
                shopifyGraphqlIdToAdminId(product.id),
                GeneratedSubscriptionType.Product,
              )}
              onSubscribe={() => void refetch()}
              product={product}
            />
          );
        })}
      </SbEditable>
    </Grid>
  );
};
